<template>
    <section class="lti-manual">

        <!-- ── 页眉 ────────────────────────────────────── -->
        <header class="mn-head">
            <div class="mn-head-main">
                <p class="mn-eyebrow">对接手册 · 管理员篇 / Administrator Guide</p>
                <h1>腾讯会议 LTI 对接手册</h1>
                <p class="mn-lede">
                    把一套 Moodle 站点接入本系统，需要在两边各配一次、互换五个值。
                    本手册按实际操作顺序给出每个字段的中英文界面名、要填的确切内容，以及做错时会看到的报错。
                </p>
            </div>
            <div class="mn-head-side">
                <el-button size="small" icon="el-icon-printer" @click="handlePrint">打印 / 存 PDF</el-button>
            </div>
        </header>

        <dl class="mn-meta">
            <div><dt>工具地址 Tool</dt><dd>{{ base }}</dd></div>
            <div><dt>协议 Spec</dt><dd>LTI 1.3 Advantage</dd></div>
            <div><dt>平台要求 Platform</dt><dd>Moodle 3.9+ / HTTPS</dd></div>
            <div><dt>适用 Scope</dt><dd>站点管理员 + 实施工程师</dd></div>
        </dl>

        <div class="mn-body">

            <!-- ── 目录 ────────────────────────────────── -->
            <nav class="mn-rail">
                <h2>目录 Contents</h2>
                <ol>
                    <li v-for="item in toc" :key="item.id">
                        <a :href="'#' + item.id" @click.prevent="goto(item.id)">
                            <span class="n">{{ item.n }}</span><span>{{ item.label }}</span>
                        </a>
                    </li>
                </ol>
            </nav>

            <!-- ── 正文 ────────────────────────────────── -->
            <div class="mn-content">

                <!-- ══ 总览 ══ -->
                <section id="mn-overview">
                    <div class="mn-sec-head">
                        <span class="mn-num">00</span>
                        <h2>对接总览<span class="en">How the pieces fit</span></h2>
                    </div>

                    <p>
                        三方参与：Moodle 是<strong>平台（Platform）</strong>，负责认证师生身份；本系统是<strong>工具（Tool）</strong>，
                        负责排会议、管录制；腾讯会议企业账号提供实际的会议能力和通讯录。教师从课程里点开活动，工具通过 LTI 1.3
                        拿到他的身份，再用租户配置里的腾讯凭据替他建会议。
                    </p>

                    <div class="mn-topo">
                        <div class="node">
                            <span class="role">平台 Platform</span>
                            <span class="who">Moodle 站点</span>
                            <span class="addr">学校自有域名</span>
                        </div>
                        <div class="link">
                            <span class="arrow">→</span>
                            <span class="what">LTI 1.3 Launch</span>
                        </div>
                        <div class="node tool">
                            <span class="role">工具 Tool</span>
                            <span class="who">LTI 会议工具</span>
                            <span class="addr">{{ host }}</span>
                        </div>
                        <div class="link">
                            <span class="arrow">→</span>
                            <span class="what">REST + 签名</span>
                        </div>
                        <div class="node">
                            <span class="role">会议服务 Service</span>
                            <span class="who">腾讯会议企业版</span>
                            <span class="addr">api.meeting.qq.com</span>
                        </div>
                    </div>
                    <p class="mn-cap">
                        工具是<strong>多租户</strong>的：一套程序服务多所学校，靠 Moodle 送来的 <code>iss</code>（平台标识）区分是哪家，
                        再取出该租户自己的腾讯会议凭据。所以「接入一所新学校」＝「在两边各配一次，然后在系统里建一条租户记录」。
                    </p>

                    <h3>要交换的东西 <span class="en">What gets exchanged</span></h3>
                    <div class="mn-tw">
                        <table>
                            <thead>
                            <tr><th>方向</th><th>内容</th><th>在哪一步</th></tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td><span class="chip chip-in">→ 填进 Moodle</span></td>
                                <td>工具的三个 URL：启动登录、重定向、公钥集</td>
                                <td class="note">步骤 1</td>
                            </tr>
                            <tr>
                                <td><span class="chip chip-out">← 取自 Moodle</span></td>
                                <td>平台的五个值：Platform ID、Client ID、认证 / 令牌 / 公钥集 URL</td>
                                <td class="note">步骤 2</td>
                            </tr>
                            <tr>
                                <td><span class="chip chip-warn">另行索取</span></td>
                                <td>腾讯会议企业版的 AppId、SecretId、SecretKey、SDKID，以及通讯录（IDaaS）凭据</td>
                                <td class="note">步骤 3</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <!-- ══ 前置条件 ══ -->
                <section id="mn-prereq">
                    <div class="mn-sec-head">
                        <span class="mn-num">0</span>
                        <h2>开始之前<span class="en">Prerequisites</span></h2>
                    </div>

                    <ul class="mn-list">
                        <li>Moodle <strong>3.9 或更高</strong>，且站点已启用 HTTPS。LTI 1.3 的 JWT 校验依赖可信证书，HTTP 站点无法对接。</li>
                        <li>你拥有 Moodle 的<strong>站点管理员</strong>权限。课程层面的教师权限不够——注册外部工具是站点级操作。</li>
                        <li>学校已开通<strong>腾讯会议企业版</strong>，并在腾讯会议管理后台申请到 API 凭据：AppId、SecretId、SecretKey、SDKID。</li>
                        <li>要排会议的<strong>教师账号必须已存在于腾讯会议企业通讯录中</strong>，且能与 Moodle 用户名对应。这是最常见的上线卡点——工具不会替你创建腾讯会议用户。</li>
                        <li>你拥有本系统的<strong>超级管理员</strong>账号（能看到「系统管理 › 租户管理」即可）。</li>
                    </ul>

                    <div class="mn-callout mn-callout-info">
                        <span class="label">账号对应关系 Identity mapping</span>
                        <p>
                            工具用 Moodle 送来的 <code>ext.user_username</code>（即 Moodle 的「用户名 / username」）去匹配腾讯会议账号，
                            <strong>不是</strong>用邮箱、也不是用姓名。上线前请确认：<strong>Moodle 用户名 = 腾讯会议通讯录里的账号</strong>。
                            西浦环境额外经过一层 AD 账号表做转换，其他学校默认直接取用户名。
                        </p>
                    </div>
                </section>

                <!-- ══ 步骤 1 ══ -->
                <section id="mn-step1">
                    <div class="mn-sec-head">
                        <span class="mn-num">1</span>
                        <h2>在 Moodle 里手动注册外部工具<span class="en">Register the tool in Moodle</span></h2>
                    </div>

                    <div class="mn-path">
                        <b>网站管理</b><i>›</i><b>插件</b><i>›</i><b>活动模块</b><i>›</i><b>外部工具</b><i>›</i><b>管理工具</b><i>›</i><b>手动配置一个工具</b>
                        <span class="en">Site administration › Plugins › Activity modules › External tool › Manage tools › configure a tool manually</span>
                    </div>

                    <h3>基本配置 <span class="en">Tool settings</span></h3>
                    <div class="mn-tw">
                        <table>
                            <thead>
                            <tr><th>字段</th><th>填什么</th><th>说明</th></tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td class="fld">工具名称<span class="en">Tool name</span></td>
                                <td class="val">Tencent Meeting</td>
                                <td class="note">教师在「添加活动」列表里看到的名字，可自定。</td>
                            </tr>
                            <tr>
                                <td class="fld">工具网址<span class="en">Tool URL</span></td>
                                <td class="val">{{ base }}</td>
                                <td class="note">只填域名，不带路径。Moodle 用它做域名归属判断。</td>
                            </tr>
                            <tr>
                                <td class="fld">LTI 版本<span class="en">LTI version</span></td>
                                <td class="val">LTI 1.3</td>
                                <td class="note"><span class="req">必须</span>是 1.3。选了 1.1 / 2.0 下面的字段不会出现。</td>
                            </tr>
                            <tr>
                                <td class="fld">公钥类型<span class="en">Public key type</span></td>
                                <td class="val">密钥集网址 / Keyset URL</td>
                                <td class="note">不要选「RSA 密钥」。用 URL 才能在工具公钥轮换时自动跟上。</td>
                            </tr>
                            <tr>
                                <td class="fld">公共密钥集<span class="en">Public keyset</span></td>
                                <td class="val">{{ ep.jwks }}</td>
                                <td class="note">Moodle 从这里取工具公钥，验证工具回传的签名。</td>
                            </tr>
                            <tr>
                                <td class="fld">发起登录网址<span class="en">Initiate login URL</span></td>
                                <td class="val">{{ ep.login }}</td>
                                <td class="note">OIDC 第一跳的落点。</td>
                            </tr>
                            <tr>
                                <td class="fld">重定向 URI<span class="en">Redirection URI(s)</span></td>
                                <td class="val">{{ ep.login }}<br>{{ ep.extra }}</td>
                                <td class="note"><span class="req">两行都要填</span>，一行一个。缺第二行会导致深度链接（内容选择）报重定向不匹配。</td>
                            </tr>
                            <tr>
                                <td class="fld">自定义参数<span class="en">Custom parameters</span></td>
                                <td class="val">（留空）</td>
                                <td class="note">工具不读自定义参数。<span class="req">切勿</span>在此覆盖 <code>user_username</code>，会打断账号匹配。</td>
                            </tr>
                            <tr>
                                <td class="fld">工具配置用法<span class="en">Tool configuration usage</span></td>
                                <td class="val">在活动选择器中显示，同时作为预配置工具</td>
                                <td class="note">英文界面为 <em>Show in activity chooser and as a preconfigured tool</em>。选其他值时教师在「添加活动」里找不到它。</td>
                            </tr>
                            <tr>
                                <td class="fld">默认启动容器<span class="en">Default launch container</span></td>
                                <td class="val">嵌入，无区块 / Embed without blocks</td>
                                <td class="note">给会议列表页留出完整宽度。若浏览器第三方 Cookie 策略导致会话丢失，改成「新窗口」可绕开。</td>
                            </tr>
                            <tr>
                                <td class="fld">支持深度链接<span class="en">Supports Deep Linking</span></td>
                                <td class="val">✓ 勾选</td>
                                <td class="note">勾上后下面的「内容选择网址」才会出现。</td>
                            </tr>
                            <tr>
                                <td class="fld">内容选择网址<span class="en">Content Selection URL</span></td>
                                <td class="val">{{ ep.extra }}</td>
                                <td class="note">教师在课程里选内容时落到这个页面。</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <h3>隐私设置 <span class="en">Privacy — 展开「显示更多」才能看到</span></h3>
                    <p class="muted">这一组默认是关的，<strong>不打开对接必然失败</strong>。工具需要姓名和邮箱来建立教师档案。</p>
                    <div class="mn-tw">
                        <table>
                            <thead>
                            <tr><th>字段</th><th>填什么</th><th>说明</th></tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td class="fld">与工具共享启动者的姓名<span class="en">Share launcher's name with tool</span></td>
                                <td><span class="tick">✓</span> <span class="req">必须勾选</span></td>
                                <td class="note">工具读取 <code>name</code> claim 写入教师档案。</td>
                            </tr>
                            <tr>
                                <td class="fld">与工具共享启动者的邮箱<span class="en">Share launcher's email with tool</span></td>
                                <td><span class="tick">✓</span> <span class="req">必须勾选</span></td>
                                <td class="note">工具读取 <code>email</code> claim，用于会议通知与档案。</td>
                            </tr>
                            <tr>
                                <td class="fld">接受来自此工具的成绩<span class="en">Accept grades from the tool</span></td>
                                <td class="val">委托给教师 / Delegate to teacher</td>
                                <td class="note">本工具不回传成绩，选什么都不影响。保持默认即可。</td>
                            </tr>
                            <tr>
                                <td class="fld">强制 SSL<span class="en">Force SSL</span></td>
                                <td class="val">不勾选</td>
                                <td class="note">站点本就是 HTTPS，无需额外强制。</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <h3>服务 <span class="en">Services</span></h3>
                    <p class="muted">
                        三项 LTI Advantage 服务——<strong>名单服务（Names and Role Provisioning）</strong>、
                        <strong>成绩同步（Assignment and Grade）</strong>、<strong>工具设置（Tool Settings）</strong>——
                        本工具都不使用，<strong>全部保持「不使用 / Do not use」</strong>。开启不会报错，但会让 Moodle 多申请无意义的权限。
                    </p>

                    <div class="mn-callout">
                        <span class="label">保存后不要关页面</span>
                        <p>点「保存更改」后工具会出现在「管理工具」的卡片列表里，下一步要从这张卡片上取值。</p>
                    </div>
                </section>

                <!-- ══ 步骤 2 ══ -->
                <section id="mn-step2">
                    <div class="mn-sec-head">
                        <span class="mn-num">2</span>
                        <h2>从 Moodle 取回五个值<span class="en">Collect the platform credentials</span></h2>
                    </div>

                    <div class="mn-path">
                        <b>管理工具</b><i>›</i>找到刚建的工具卡片<i>›</i>右上角 <b>⋮ / 齿轮</b><i>›</i><b>查看配置详情</b>
                        <span class="en">Manage tools › the tool card › ⋮ › View configuration details</span>
                    </div>

                    <p>Moodle 会弹出一个只读面板。把下面五个值抄下来——它们是步骤 3 建租户时要填的内容。</p>

                    <div class="mn-tw">
                        <table>
                            <thead>
                            <tr><th>Moodle 显示名</th><th>对应租户字段</th><th>形态与注意事项</th></tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td class="fld">平台 ID<span class="en">Platform ID</span></td>
                                <td class="val">iss</td>
                                <td class="note">形如 <code>https://moodle.example.edu.cn</code>。<span class="req">必须与 Moodle 的 <code>$CFG-&gt;wwwroot</code> 逐字符一致</span>：含协议、不含结尾斜杠、区分大小写。</td>
                            </tr>
                            <tr>
                                <td class="fld">客户端 ID<span class="en">Client ID</span></td>
                                <td class="val">client_id</td>
                                <td class="note">15 位随机串。<strong>每重新注册一次工具就会换一个</strong>，换了必须同步改租户。</td>
                            </tr>
                            <tr>
                                <td class="fld">认证请求网址<span class="en">Authentication request URL</span></td>
                                <td class="val">authentication_request_url</td>
                                <td class="note">通常是 <code>{平台 ID}/mod/lti/auth.php</code>。</td>
                            </tr>
                            <tr>
                                <td class="fld">访问令牌网址<span class="en">Access token URL</span></td>
                                <td class="val">access_token_url</td>
                                <td class="note">通常是 <code>{平台 ID}/mod/lti/token.php</code>。</td>
                            </tr>
                            <tr>
                                <td class="fld">公钥集网址<span class="en">Public keyset URL</span></td>
                                <td class="val">public_keyset_url</td>
                                <td class="note">通常是 <code>{平台 ID}/mod/lti/certs.php</code>。工具用它验证 Moodle 送来的 JWT。</td>
                            </tr>
                            <tr>
                                <td class="fld">部署 ID<span class="en">Deployment ID</span></td>
                                <td class="note" colspan="2">本工具<strong>不校验</strong>部署 ID，租户里没有对应字段，无需填写。仍建议记录备查——将来若启用名单服务会用到。</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="mn-callout">
                        <span class="label">iss 和 client_id 是最容易出事的两个值</span>
                        <p>
                            工具完全靠 <code>iss</code> 精确匹配来判断「这是哪所学校」。
                            <strong>Moodle 换域名、或在 Moodle 里删掉工具重新注册过</strong>，都会让租户里的记录失效，
                            表现为所有启动立刻失败并显示 <code>is not registered!</code>。
                            这两种情况都请回到「系统管理 › 租户管理」同步更新。
                        </p>
                    </div>
                </section>

                <!-- ══ 步骤 3 ══ -->
                <section id="mn-step3">
                    <div class="mn-sec-head">
                        <span class="mn-num">3</span>
                        <h2>在本系统创建租户<span class="en">Create the tenant</span></h2>
                    </div>

                    <div class="mn-path">
                        <b>系统管理</b><i>›</i><b>租户管理</b><i>›</i><b>新建</b>
                        <span class="en">System › Tenants › New</span>
                    </div>

                    <h3>身份 <span class="en">Identity</span></h3>
                    <div class="mn-tw">
                        <table>
                            <thead><tr><th>字段</th><th>填什么</th><th>说明</th></tr></thead>
                            <tbody>
                            <tr>
                                <td class="fld">编码<span class="en">code</span></td>
                                <td class="val">如 xjtlu</td>
                                <td class="note">全站唯一的短标识，小写英文。<span class="req">见下方告警</span>——这个值会影响程序去找哪个租户实现类。</td>
                            </tr>
                            <tr>
                                <td class="fld">名称<span class="en">name</span></td>
                                <td class="val">如 西交利物浦大学</td>
                                <td class="note">仅用于后台列表显示。</td>
                            </tr>
                            <tr>
                                <td class="fld">租户实现类<span class="en">tenant_class</span></td>
                                <td class="val">XjtluTenantApiClass</td>
                                <td class="note">决定账号如何映射到腾讯会议用户。新学校若无特殊需求，<strong>直接填 <code>XjtluTenantApiClass</code></strong> 复用现成实现。</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="mn-callout">
                        <span class="label">tenant_class 留空的陷阱</span>
                        <p>
                            <code>tenant_class</code> 留空时，程序会按<strong>「首字母大写(code) + TenantApiClass」</strong>去找类——
                            比如 code 填 <code>fudan</code> 就会去找 <code>FudanTenantApiClass</code>。
                            <strong>该类不存在时不会降级，会直接报 500</strong>。
                            所以：要么显式填一个已存在的类名，要么先让开发在 <code>app/Services/Tenants/</code> 下加好类再建租户。
                        </p>
                    </div>

                    <h3>LTI 对接 <span class="en">LTI — 来自步骤 2</span></h3>
                    <div class="mn-tw">
                        <table>
                            <thead><tr><th>字段</th><th>来源</th><th>形态</th></tr></thead>
                            <tbody>
                            <tr><td class="fld">iss</td><td class="note">Moodle 的 Platform ID</td><td class="val">https://moodle.example.edu.cn</td></tr>
                            <tr><td class="fld">client_id</td><td class="note">Moodle 的 Client ID</td><td class="val">15 位随机串</td></tr>
                            <tr><td class="fld">auth_url<span class="en">authentication_request_url</span></td><td class="note">Moodle 的认证请求网址</td><td class="val">…/mod/lti/auth.php</td></tr>
                            <tr><td class="fld">token_url<span class="en">access_token_url</span></td><td class="note">Moodle 的访问令牌网址</td><td class="val">…/mod/lti/token.php</td></tr>
                            <tr><td class="fld">keyset_url<span class="en">public_keyset_url</span></td><td class="note">Moodle 的公钥集网址</td><td class="val">…/mod/lti/certs.php</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <h3>腾讯会议凭据 <span class="en">Tencent Meeting</span></h3>
                    <p class="muted">四个字段全部<span class="req">必填</span>，来自腾讯会议企业管理后台的「企业应用 / API 凭据」。</p>
                    <div class="mn-tw">
                        <table>
                            <thead><tr><th>字段</th><th>说明</th></tr></thead>
                            <tbody>
                            <tr><td class="fld">腾讯 AppId<span class="en">tencent_app_id</span></td><td class="note">企业标识，9 位数字。</td></tr>
                            <tr><td class="fld">secret_id</td><td class="note">API 调用的密钥 ID。</td></tr>
                            <tr><td class="fld">secret_key</td><td class="note">API 签名密钥。<strong>写入后不再回显</strong>，请自行留底。</td></tr>
                            <tr><td class="fld">sdkid</td><td class="note">应用 SDKID，11 位数字。</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <h3>通讯录 IDaaS <span class="en">Contacts</span></h3>
                    <div class="mn-tw">
                        <table>
                            <thead><tr><th>字段</th><th>说明</th></tr></thead>
                            <tbody>
                            <tr><td class="fld">idaas_url<span class="en">idaas_base_url</span></td><td class="note">腾讯会议通讯录接口，形如 <code>https://{企业}-admin.id.meeting.qq.com/contacts/</code>。</td></tr>
                            <tr><td class="fld">idaas_key<span class="en">idaas_jwt_key</span></td><td class="note">通讯录接口的 JWT 签名密钥。</td></tr>
                            <tr><td class="fld">root_dept<span class="en">idaas_root_department</span></td><td class="note"><strong>留空即可</strong>——首次调用时程序会自动查询根部门 ID 并回写。手工填错会导致新建教师落到错误部门。</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <p class="muted">
                        「煜复 SDK」那一组字段（<code>yufu_sdk_*</code>）是另一条业务线用的，LTI 对接<strong>不需要填</strong>，留空。
                    </p>
                </section>

                <!-- ══ 步骤 4 ══ -->
                <section id="mn-step4">
                    <div class="mn-sec-head">
                        <span class="mn-num">4</span>
                        <h2>联调验证<span class="en">Verify the integration</span></h2>
                    </div>

                    <p>按顺序做完这五项，对接才算通。任一项失败请直接跳到<a href="#mn-step5" @click.prevent="goto('mn-step5')">报错对照</a>。</p>

                    <h3>① 确认工具公钥可达</h3>
                    <p class="muted">在任意能上网的机器执行：</p>
                    <div class="mn-path mn-code">curl {{ ep.jwks }}</div>
                    <p class="muted">应返回一段含 <code>"keys"</code> 的 JSON。返回空或超时说明工具侧异常，先别继续。</p>

                    <h3>② 建一门测试课并添加活动</h3>
                    <p class="muted">
                        新建测试课程 → 开启编辑 → <strong>添加活动或资源</strong> → 在列表里找到步骤 1 起的工具名
                        （若找不到，回步骤 1 检查「工具配置用法」）→ 命名后保存。
                    </p>

                    <h3>③ 用教师账号进入</h3>
                    <p class="muted">
                        应看到会议列表页，顶部三个页签 <strong>进行中 / 已排期 / 已结束（Ongoing / Scheduled / Completed）</strong>，
                        右上角有<strong>「新建」按钮</strong>。看到按钮＝身份识别、腾讯会议用户匹配全部成功。
                    </p>

                    <h3>④ 用学生账号进入</h3>
                    <p class="muted">
                        应看到同一张列表，但<strong>没有「新建」按钮</strong>，也不能编辑或删除。
                        工具只为教师建立档案和腾讯会议用户，学生仅做只读访问。
                    </p>

                    <h3>⑤ 建一场测试会议</h3>
                    <p class="muted">教师点「新建」，填主题、日期时间、时长后提交。注意工具侧有几条硬性校验：</p>
                    <ul class="mn-list">
                        <li>开始时间的<strong>分钟必须是 00 或 30</strong>；</li>
                        <li>时长<strong>不得超过 24 小时</strong>；</li>
                        <li>会议密码若填，<strong>必须是 6 位数字</strong>；</li>
                        <li>开始时间必须晚于当前时间。</li>
                    </ul>
                    <p class="muted">
                        提交成功后列表里会出现会议号和入会链接，说明腾讯会议 API 调通了。
                        全过程的 API 往返记录在<strong>「系统管理 › 系统日志」</strong>，排查时按 <code>TMAPIERROR</code> 检索。
                    </p>
                </section>

                <!-- ══ 步骤 5 ══ -->
                <section id="mn-step5">
                    <div class="mn-sec-head">
                        <span class="mn-num">5</span>
                        <h2>报错对照<span class="en">Troubleshooting</span></h2>
                    </div>

                    <div class="mn-tw">
                        <table>
                            <thead>
                            <tr><th>看到什么</th><th>原因</th><th>怎么处理</th></tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td class="val">is not registered!</td>
                                <td class="note">租户表里没有匹配该 <code>iss</code> 的记录。</td>
                                <td class="note">核对租户 <code>iss</code> 与 Moodle 的 <code>$CFG-&gt;wwwroot</code>：协议、大小写、结尾斜杠必须完全一致。Moodle 换过域名是最常见诱因。</td>
                            </tr>
                            <tr>
                                <td class="val">LTI Launch Data is empty</td>
                                <td class="note">启动数据里缺姓名、邮箱或 <code>user_username</code>。</td>
                                <td class="note">回步骤 1 的隐私设置，确认两个「共享」开关都已勾选；同时检查自定义参数没有覆盖 <code>user_username</code>。</td>
                            </tr>
                            <tr>
                                <td class="val">… is not a valid tencent meeting user!</td>
                                <td class="note">该教师不在腾讯会议企业通讯录中，或用户名对不上。</td>
                                <td class="note">在腾讯会议后台把该账号加入通讯录，或核对「Moodle 用户名 = 腾讯会议账号」。西浦环境还需确认 AD 账号表里有对应记录。</td>
                            </tr>
                            <tr>
                                <td class="val">This teacher is not tencent meeting user.</td>
                                <td class="note">教师档案存在但未绑定腾讯会议用户。</td>
                                <td class="note">到「后台管理 › 腾讯用户」检查该用户；必要时删除其档案后让他重新从 Moodle 进入以触发重建。</td>
                            </tr>
                            <tr>
                                <td class="val">HTTP 405</td>
                                <td class="note">用 GET 请求了 <code>/lti/login</code>，而工具只接受 POST。</td>
                                <td class="note">正常启动一定是 POST。出现 405 通常是有人直接在浏览器地址栏打开了该 URL，不是配置问题。</td>
                            </tr>
                            <tr>
                                <td class="val">反复跳回登录页 / 会话丢失</td>
                                <td class="note">浏览器拦截了跨站 Cookie。</td>
                                <td class="note">工具已设 <code>SameSite=None; Secure</code>。若仍失败，把 Moodle 的「默认启动容器」改为<strong>新窗口</strong>，或让用户允许第三方 Cookie。Safari 与无痕模式最容易触发。</td>
                            </tr>
                            <tr>
                                <td class="val">页面能开，但会议建不出来</td>
                                <td class="note">腾讯会议凭据错误或权限不足。</td>
                                <td class="note">查「系统日志」中 <code>TMAPIERROR</code> 条目，里面有腾讯返回的原始错误码与 Trace Id，可直接提工单。</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <!-- ══ 附录 A ══ -->
                <section id="mn-appx-a">
                    <div class="mn-sec-head">
                        <span class="mn-num">A</span>
                        <h2>端点速查<span class="en">Endpoint reference</span></h2>
                    </div>

                    <p class="muted">本系统对外暴露的全部地址，供填表时复制。</p>
                    <div class="mn-ep">
                        <div v-for="row in endpoints" :key="row.url">
                            <span class="u">
                                {{ row.url }}
                                <el-button type="text" class="cp" icon="el-icon-document-copy"
                                           @click="copy(row.url)">复制</el-button>
                            </span>
                            <span class="d">{{ row.desc }}</span>
                        </div>
                    </div>
                </section>

                <!-- ══ 附录 B ══ -->
                <section id="mn-appx-b">
                    <div class="mn-sec-head">
                        <span class="mn-num">B</span>
                        <h2>核对已接入的学校<span class="en">Check the live tenants</span></h2>
                    </div>

                    <p>
                        每所已接入学校的 <code>iss</code>、<code>client_id</code> 和腾讯凭据都在「系统管理 › 租户管理」里。
                        对接出问题时，第一件事永远是回那里比对这两个值和 Moodle 当前的实际配置——
                        <strong>它们是整条链路上唯一会「悄悄过期」的东西</strong>。
                    </p>

                    <el-button type="primary" size="small" icon="el-icon-school"
                               @click="$router.push('/admin/tenant')">前往租户管理
                    </el-button>
                </section>

                <p class="mn-foot">
                    字段名与取值以本系统生产配置为准；Moodle 界面名称以 4.x 中英文界面为准，不同小版本措辞可能略有差异。
                </p>

            </div>
        </div>
    </section>
</template>

<script>
export default {
    name: "manual-lti",
    data() {
        return {
            toc: [
                {id: 'mn-overview', n: '00', label: '对接总览'},
                {id: 'mn-prereq', n: '0', label: '前置条件'},
                {id: 'mn-step1', n: '1', label: 'Moodle 注册工具'},
                {id: 'mn-step2', n: '2', label: '取回五个值'},
                {id: 'mn-step3', n: '3', label: '创建租户'},
                {id: 'mn-step4', n: '4', label: '联调验证'},
                {id: 'mn-step5', n: '5', label: '报错对照'},
                {id: 'mn-appx-a', n: 'A', label: '端点速查'},
                {id: 'mn-appx-b', n: 'B', label: '核对租户'},
            ],
        }
    },
    computed: {
        base() {
            const url = this.$organization.host_url || (window.location.origin + '/');
            return url.replace(/\/+$/, '');
        },
        host() {
            return this.base.replace(/^https?:\/\//, '');
        },
        ep() {
            return {
                jwks: this.base + '/lti/jwks',
                login: this.base + '/lti/login',
                extra: this.base + '/lti/extra',
                meetings: this.base + '/lti/meetings',
                admin: this.base + '/admin',
            }
        },
        endpoints() {
            return [
                {url: this.ep.jwks, desc: '工具公钥集（JWKS）——填入 Moodle 的「公共密钥集」'},
                {url: this.ep.login, desc: 'OIDC 发起登录 + 启动落点——填入「发起登录网址」和「重定向 URI」第一行'},
                {url: this.ep.extra, desc: '深度链接 / 内容选择——填入「内容选择网址」和「重定向 URI」第二行'},
                {url: this.ep.meetings, desc: '启动成功后的会议列表页，由工具自行跳转，无需填写'},
                {url: this.ep.admin, desc: '管理后台（租户、资源、用户、日志）'},
            ]
        },
    },
    methods: {
        goto(id) {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
        },
        copy(text) {
            this.$copyText(text).then(() => {
                this.$message.success('已复制：' + text);
            }).catch(() => {
                this.$message.error('复制失败，请手动选取');
            });
        },
        handlePrint() {
            window.print();
        },
    },
}
</script>

<style scoped lang="scss">
/* 配色沿用 Element UI 的语义色，让手册读起来是后台的一部分，而不是一份外来文档 */
$ink: #303133;
$ink-2: #5A6069;
$ink-3: #909399;
$line: #DCDFE6;
$line-soft: #EBEEF5;
$surface: #FFFFFF;
$surface-2: #F5F7FA;
$accent: #409EFF;
$accent-ink: #2A6FB8;
$accent-soft: #ECF5FF;
$warn: #B8791B;
$warn-soft: #FDF6EC;
$warn-line: #F5DAB1;
$ok: #529B2E;
$ok-soft: #F0F9EB;

$mono: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace;

.lti-manual {
    color: $ink;
    font-size: 14px;
    line-height: 1.75;
    padding-bottom: 40px;

    /* ── 页眉 ── */
    .mn-head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
        flex-wrap: wrap;
    }

    .mn-eyebrow {
        font-size: 11px;
        letter-spacing: .14em;
        text-transform: uppercase;
        color: $accent;
        font-weight: 600;
        margin: 0 0 8px;
    }

    h1 {
        font-size: 26px;
        font-weight: 600;
        line-height: 1.25;
        margin: 0 0 10px;
        color: $ink;
    }

    .mn-lede {
        max-width: 62em;
        color: $ink-2;
        margin: 0;
    }

    .mn-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 32px;
        margin: 20px 0 0;
        padding: 16px 0;
        border-top: 1px solid $line-soft;
        border-bottom: 1px solid $line-soft;

        div { display: flex; flex-direction: column; }
        dt {
            font-size: 10.5px;
            letter-spacing: .12em;
            text-transform: uppercase;
            color: $ink-3;
            font-weight: 600;
        }
        dd { margin: 0; font-family: $mono; font-size: 12.5px; color: $ink; }
    }

    /* ── 骨架 ── */
    .mn-body {
        display: flex;
        align-items: flex-start;
        gap: 40px;
        padding-top: 28px;
    }

    .mn-rail {
        flex: 0 0 170px;
        position: sticky;
        top: 0;
        font-size: 13px;

        h2 {
            font-size: 10.5px;
            letter-spacing: .13em;
            text-transform: uppercase;
            color: $ink-3;
            font-weight: 600;
            margin: 0 0 10px;
        }
        ol { list-style: none; margin: 0; padding: 0; }
        li { margin: 0; }
        a {
            display: flex;
            gap: 8px;
            padding: 4px 8px 4px 0;
            color: $ink-2;
            text-decoration: none;
            border-radius: 3px;
            line-height: 1.5;

            &:hover { color: $accent; background: $accent-soft; padding-left: 8px; }
        }
        .n {
            flex: 0 0 20px;
            font-family: $mono;
            font-size: 11px;
            color: $ink-3;
        }
        a:hover .n { color: $accent; }
    }

    .mn-content { flex: 1; min-width: 0; }

    /* ── 章节 ── */
    section { margin-bottom: 46px; scroll-margin-top: 12px; }

    .mn-sec-head {
        display: flex;
        align-items: center;
        gap: 14px;
        padding-bottom: 10px;
        margin-bottom: 18px;
        border-bottom: 2px solid $ink;
    }

    .mn-num {
        font-family: $mono;
        font-size: 12px;
        font-weight: 600;
        color: $accent-ink;
        background: $accent-soft;
        border-radius: 3px;
        padding: 2px 8px;
        flex: 0 0 auto;
    }

    h2 {
        font-size: 19px;
        font-weight: 600;
        margin: 0;
        line-height: 1.3;

        .en {
            display: block;
            font-size: 11.5px;
            font-weight: 400;
            letter-spacing: .06em;
            text-transform: uppercase;
            color: $ink-3;
            margin-top: 3px;
        }
    }

    h3 {
        font-size: 15px;
        font-weight: 600;
        margin: 26px 0 10px;

        .en { font-weight: 400; color: $ink-3; font-size: 12.5px; }
    }

    p { max-width: 68em; margin: 0 0 12px; }
    .muted { color: $ink-2; }
    .mn-cap { font-size: 13px; color: $ink-2; max-width: 68em; }

    .mn-list {
        max-width: 68em;
        margin: 0 0 14px;
        padding-left: 20px;

        li { margin-bottom: 6px; }
    }

    code {
        font-family: $mono;
        font-size: .88em;
        background: $surface-2;
        border: 1px solid $line-soft;
        border-radius: 3px;
        padding: .1em .36em;
        word-break: break-all;
        color: $ink;
    }

    a { color: $accent; }

    /* ── 操作路径 ── */
    .mn-path {
        font-family: $mono;
        font-size: 12.5px;
        line-height: 1.85;
        background: $surface;
        border: 1px solid $line;
        border-left: 3px solid $accent;
        border-radius: 0 4px 4px 0;
        padding: 10px 14px;
        margin: 0 0 16px;
        color: $ink-2;
        overflow-x: auto;

        b { color: $ink; font-weight: 500; }
        i { color: $ink-3; padding: 0 4px; font-style: normal; }
        .en { display: block; color: $ink-3; font-size: 11.5px; margin-top: 2px; }
    }
    .mn-code { color: $ink; border-left-color: $ok; }

    /* ── 表格 ── */
    .mn-tw {
        overflow-x: auto;
        margin: 0 0 16px;
        border: 1px solid $line;
        border-radius: 4px;
        background: $surface;
    }

    table {
        border-collapse: collapse;
        width: 100%;
        font-size: 13px;
        min-width: 540px;
    }

    thead th {
        text-align: left;
        font-size: 10.5px;
        letter-spacing: .12em;
        text-transform: uppercase;
        color: $ink-3;
        font-weight: 600;
        padding: 9px 14px;
        background: $surface-2;
        border-bottom: 1px solid $line;
        white-space: nowrap;
    }

    tbody td {
        padding: 10px 14px;
        border-bottom: 1px solid $line-soft;
        vertical-align: top;
        line-height: 1.6;
    }
    tbody tr:last-child td { border-bottom: 0; }

    .fld {
        font-weight: 500;
        min-width: 140px;

        .en {
            display: block;
            font-weight: 400;
            font-size: 11px;
            color: $ink-3;
            margin-top: 1px;
        }
    }
    .val { font-family: $mono; font-size: 12px; word-break: break-all; color: $accent-ink; }
    td.note { color: $ink-2; font-size: 12.5px; }
    .req { color: $warn; font-weight: 500; }
    .tick { color: $ok; font-weight: 700; }

    /* ── 标记 ── */
    .chip {
        display: inline-block;
        font-family: $mono;
        font-size: 10.5px;
        padding: 1px 7px;
        border-radius: 3px;
        white-space: nowrap;
    }
    .chip-in { background: $accent-soft; color: $accent-ink; }
    .chip-out { background: $ok-soft; color: $ok; }
    .chip-warn { background: $warn-soft; color: $warn; }

    /* ── 提示块 ── */
    .mn-callout {
        border: 1px solid $warn-line;
        background: $warn-soft;
        border-radius: 4px;
        padding: 12px 16px;
        margin: 0 0 16px;
        max-width: 74em;

        p:last-child { margin-bottom: 0; }

        .label {
            display: block;
            font-size: 10.5px;
            letter-spacing: .13em;
            text-transform: uppercase;
            color: $warn;
            font-weight: 600;
            margin-bottom: 4px;
        }
        code { background: rgba(184, 121, 27, .09); border-color: $warn-line; }
    }

    .mn-callout-info {
        border-color: $line;
        background: $surface;

        .label { color: $accent; }
        code { background: $surface-2; border-color: $line-soft; }
    }

    /* ── 拓扑图 ── */
    .mn-topo {
        display: flex;
        align-items: stretch;
        margin: 0 0 10px;
        border: 1px solid $line;
        border-radius: 4px;
        background: $surface;
        overflow: hidden;

        .node {
            flex: 1;
            padding: 14px 14px;
            display: flex;
            flex-direction: column;
            gap: 3px;
            min-width: 0;

            &.tool { background: $accent-soft; }
            &.tool .role { color: $accent-ink; }

            .role {
                font-size: 10px;
                letter-spacing: .13em;
                text-transform: uppercase;
                color: $ink-3;
                font-weight: 600;
            }
            .who { font-weight: 500; font-size: 14px; line-height: 1.35; }
            .addr { font-family: $mono; font-size: 11px; color: $ink-2; word-break: break-all; }
        }

        .link {
            flex: 0 0 auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 2px;
            padding: 0 12px;
            background: $surface-2;
            border-left: 1px solid $line;
            border-right: 1px solid $line;

            .arrow { font-family: $mono; color: $ink-3; font-size: 14px; line-height: 1; }
            .what {
                font-size: 9px;
                letter-spacing: .1em;
                text-transform: uppercase;
                color: $ink-3;
                white-space: nowrap;
            }
        }
    }

    /* ── 端点清单 ── */
    .mn-ep {
        border: 1px solid $line;
        border-radius: 4px;
        overflow: hidden;
        margin: 0 0 16px;

        > div {
            background: $surface;
            padding: 10px 14px;
            border-bottom: 1px solid $line-soft;

            &:last-child { border-bottom: 0; }
        }
        .u {
            display: block;
            font-family: $mono;
            font-size: 12.5px;
            color: $accent-ink;
            word-break: break-all;
        }
        .d { display: block; font-size: 12.5px; color: $ink-2; }
        .cp { padding: 0 0 0 8px; font-size: 12px; }
    }

    .mn-foot {
        margin-top: 34px;
        padding-top: 16px;
        border-top: 1px solid $line-soft;
        color: $ink-3;
        font-size: 12.5px;
    }

    /* ── 窄屏：目录收到正文上方 ── */
    @media (max-width: 1180px) {
        .mn-body { display: block; }
        .mn-rail {
            position: static;
            margin-bottom: 24px;
            padding-bottom: 12px;
            border-bottom: 1px solid $line-soft;

            ol { display: flex; flex-wrap: wrap; gap: 2px 4px; }
            a { padding: 3px 8px; background: $surface-2; }
        }
        .mn-topo { display: block; }
        .mn-topo .link {
            flex-direction: row;
            gap: 8px;
            padding: 5px 14px;
            border: 0;
            border-top: 1px solid $line;
            border-bottom: 1px solid $line;

            .arrow { transform: rotate(90deg); }
        }
    }
}

/* ── 打印：去掉导航和按钮，正文铺满 ── */
@media print {
    .lti-manual {
        .mn-rail, .mn-head-side, .cp { display: none !important; }
        .mn-body { display: block; }
        section { page-break-inside: auto; }
        .mn-sec-head { page-break-after: avoid; }
        .mn-tw, .mn-callout, .mn-topo { page-break-inside: avoid; }
    }
}
</style>
